// Thin fetch wrapper. Same-origin (Vite proxies /api to the server in dev), cookies
// included so the httpOnly session travels automatically.
import { API_PREFIX } from '@alignova/shared';
import { enqueue, isNetworkError, newKey } from './offlineQueue.ts';

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  /**
   * The whole error body.
   *
   * Some refusals carry more than a sentence — "there is already a clinic here, and here is which
   * one" — and the screen needs that detail to offer a sensible choice rather than just apologising.
   */
  readonly details?: unknown;
  constructor(status: number, message: string, code?: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** Sentinel returned by a queueable mutation that couldn't reach the server and was
 *  parked in the offline queue instead. The write will replay on reconnect. */
export interface Deferred {
  deferred: true;
  /** The Idempotency-Key the mutation will replay under. */
  key: string;
}

export function isDeferred(value: unknown): value is Deferred {
  return typeof value === 'object' && value !== null && (value as Deferred).deferred === true;
}

interface MutateOptions {
  /** On a network failure (offline), persist to the queue and return a Deferred
   *  instead of throwing. Only set this for mutations that are safe to replay. */
  queue?: boolean;
}

async function request<T>(
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  opts?: MutateOptions,
): Promise<T | Deferred> {
  // For queueable mutations, mint the Idempotency-Key up front and send it on the very
  // first attempt. If the response is then lost mid-flight, the queued replay reuses the
  // same key so the server dedups it — the write still lands exactly once.
  const key = opts?.queue ? newKey() : undefined;
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (key) headers['Idempotency-Key'] = key;

  let res: Response;
  try {
    res = await fetch(`${API_PREFIX}${path}`, {
      method,
      credentials: 'include',
      headers: Object.keys(headers).length ? headers : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    if (opts?.queue && isNetworkError(err)) {
      enqueue({ id: key, method, path, body });
      return { deferred: true, key: key! };
    }
    throw err;
  }
  return parse<T>(res);
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${API_PREFIX}${path}`, { method: 'GET', credentials: 'include' });
  return parse<T>(res) as Promise<T>;
}

async function parse<T>(res: Response): Promise<T> {
  const isJson = res.headers.get('content-type')?.includes('application/json');
  const data: unknown = isJson ? await res.json() : null;
  if (!res.ok) {
    const err = (data ?? {}) as { error?: string; code?: string };
    throw new ApiError(res.status, err.error ?? res.statusText, err.code, data);
  }
  return data as T;
}

// Overloads: without { queue:true } the mutation resolves to T (or throws on any
// failure); with it, a network drop resolves to a Deferred instead of rejecting.
interface Api {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  post<T>(path: string, body: unknown, opts: MutateOptions): Promise<T | Deferred>;
  put<T>(path: string, body?: unknown): Promise<T>;
  put<T>(path: string, body: unknown, opts: MutateOptions): Promise<T | Deferred>;
  patch<T>(path: string, body?: unknown): Promise<T>;
  patch<T>(path: string, body: unknown, opts: MutateOptions): Promise<T | Deferred>;
  del<T>(path: string): Promise<T>;
  del<T>(path: string, body: unknown, opts: MutateOptions): Promise<T | Deferred>;
}

export const api: Api = {
  get,
  post: <T>(path: string, body?: unknown, opts?: MutateOptions) =>
    request<T>('POST', path, body, opts),
  put: <T>(path: string, body?: unknown, opts?: MutateOptions) =>
    request<T>('PUT', path, body, opts),
  patch: <T>(path: string, body?: unknown, opts?: MutateOptions) =>
    request<T>('PATCH', path, body, opts),
  del: <T>(path: string, body?: unknown, opts?: MutateOptions) =>
    request<T>('DELETE', path, body, opts),
};
